import { useTranslation } from 'react-i18next';
import type { Wallet } from '@asia/shared';
import { haptic } from '../../hooks/useTelegram';
import { useTheme } from '../../store/theme';
import { copyText } from '../../lib/clipboard';
import { CoinIcon } from '../CoinIcon';
import { FigIcon } from '../FigIcon';
import { Sheet } from './Sheet';
import actionRowBg from '../../assets/figma/action-row-bg.png';

interface Props {
  wallet: Wallet;
  onClose: () => void;
  onSeed: () => void;
  onRename: () => void;
  onDelete: () => void;
}

function Action({
  icon,
  label,
  danger,
  onClick,
  painted,
}: {
  icon: React.ReactNode;
  label: string;
  danger?: boolean;
  onClick: () => void;
  /** True when the Figma image behind the row is drawing the button face. */
  painted: boolean;
}) {
  return (
    <button
      onClick={() => {
        haptic();
        onClick();
      }}
      className={`relative flex flex-[1_0_0] flex-col items-center justify-center gap-[2px] rounded-[20px] py-2 active:scale-[0.98] ${
        painted ? '' : 'bg-card shadow-card'
      }`}
    >
      <span className={`relative ${danger ? 'text-negative' : 'text-accent'}`}>{icon}</span>
      <span className="relative w-full self-stretch text-center text-[12px] font-medium leading-none tracking-[-0.3px] text-ink">
        {label}
      </span>
    </button>
  );
}

/** Wallet-management bottom sheet — Figma node 2:5116. Opens from the `>` on a
 *  wallet row: shows the wallet + address and Seed / Rename / Delete actions. */
export function WalletDetailSheet({ wallet, onClose, onSeed, onRename, onDelete }: Props) {
  const { t } = useTranslation();
  const painted = useTheme((s) => s.resolved) === 'light';
  return (
    <Sheet
      onClose={onClose}
      panelClassName="flex w-full flex-col items-center gap-[24px] rounded-t-[36px] bg-surface px-4 pb-[44px] pt-4"
    >
        {/* z-20 keeps the wallet name/address above the button-row background
            image, whose opaque shadow bleed extends up into this block. */}
        <div className="relative z-20 flex w-full flex-col items-center gap-5">
          <div className="flex flex-col items-center gap-[6px]">
            <p className="text-[16px] font-medium text-ink">{t('nav.wallet')}</p>
            <p className="text-[14px] text-muted">TRON</p>
          </div>

          <CoinIcon symbol="TRX" size={80} />

          <div className="flex h-[44px] flex-col items-center justify-center gap-2">
            <div className="flex items-center gap-1">
              <span className="text-[24px] font-medium text-ink">{wallet.name}</span>
              <span
                role="button"
                onClick={(e) => {
                  e.stopPropagation();
                  void copyText(wallet.address);
                }}
              >
                <FigIcon name="copy" size={22} className="text-muted" />
              </span>
            </div>
            <div className="flex items-center gap-[5px] text-[13px] text-muted">
              {wallet.address.slice(0, 4)}····{wallet.address.slice(-4)}
              <span className="inline-block size-[2px] rounded-full bg-muted" />
              TRC-20
            </div>
          </div>
        </div>

        <div className="relative flex w-full items-start gap-[10px]">
          {/* All three button faces + shadows in one piece, exported straight from
              Figma (row 2:5136 cloned without icons/labels onto a #F3F2F8 frame with
              40px shadow bleed → 450×146 @3×). One image = Figma's own shadow
              overlaps; scaled uniformly to the row width (450/370).

              The image is a light frame, so in the dark theme it would be three
              white slabs. There is no dark export, so the dark theme draws the
              faces from tokens instead — the same three buttons, lit the way the
              medium allows. */}
          {painted && (
            <span
              aria-hidden
              className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
              style={{
                width: 'calc(100% * 450 / 370)',
                aspectRatio: '450 / 146',
                backgroundImage: `url(${actionRowBg})`,
                backgroundSize: '100% 100%',
              }}
            />
          )}
          <Action
            painted={painted}
            icon={<FigIcon name="shieldSecurity" size={32} />}
            label={t('wallet.viewSeed')}
            onClick={onSeed}
          />
          <Action
            painted={painted}
            icon={<FigIcon name="edit2" size={32} />}
            label={t('wallet.rename')}
            onClick={onRename}
          />
          <Action
            painted={painted}
            icon={<FigIcon name="trash" size={32} />}
            label={t('common.delete')}
            danger
            onClick={onDelete}
          />
        </div>
    </Sheet>
  );
}
